import React from 'react';
import { Users, Mic, Building, Layers, Calendar, Award, Star, Globe, Briefcase } from 'lucide-react';
import { fileUrl } from '@/lib/api';
import { useMotion, useNumberTicker, useMagnetic, useInViewportVideo } from '@/lib/motion';
import PresentingSponsorBadge from '@/components/PresentingSponsorBadge';

const DEFAULT_SWOOGO = 'https://circle.swoogo.com/SEI_ConIII';
// v3.9 — Bellagio room-booking URL backing the BOOK ROOMS hero CTA.
// Same fallback pattern as DEFAULT_SWOOGO above: when the admin clears
// cta_book_rooms_url the button keeps working pointing at this canonical
// booking page (external, opens in a new tab).
const DEFAULT_BELLAGIO_BOOKING = 'https://www.guestreservations.com/bellagio-las-vegas/booking';
// v3.11 — Swoogo public schedule URL backing the SCHEDULE hero CTA.
// Same fallback pattern: when the admin clears cta_schedule_url the
// button keeps working pointing at this canonical Swoogo schedule page
// (external, opens in a new tab).
const DEFAULT_SCHEDULE_URL = 'https://circle.swoogo.com/sei_coniii/10027978';

// Icon picker preset (used by admin too via HERO_ICON_KEYS export)
export const HERO_ICON_KEYS = ['users', 'mic', 'building', 'layers', 'calendar', 'award', 'star', 'globe', 'briefcase'];
const iconMap = {
  users: Users,
  mic: Mic,
  building: Building,
  layers: Layers,
  calendar: Calendar,
  award: Award,
  star: Star,
  globe: Globe,
  briefcase: Briefcase,
};

// Entrance speed multipliers applied to base CSS delay (ms)
const SPEED_MULTIPLIER = { slow: 1.5, normal: 1.0, fast: 0.5, off: 0 };

function parseNumber(s) {
  if (!s) return { num: 0, suffix: '' };
  const match = String(s).match(/^([\d,]+)(.*)$/);
  if (!match) return { num: 0, suffix: String(s) };
  return { num: parseInt(match[1].replace(/,/g, ''), 10) || 0, suffix: match[2] || '' };
}
function formatNumber(n) {
  return n.toLocaleString('en-US');
}

// Fallback for the Sponsorship Opportunities CTA when the admin URL is blank.
const SPONSORSHIP_MAILTO_FALLBACK = 'mailto:tiffanys@sei-con.org?subject=Sponsorship%20Opportunities%20Inquiry';
// v3.17 — Fallback label for the third hero CTA when the admin label
// is blank. Mirrors the legacy hardcoded literal that lived in the JSX
// prior to v3.17 so the live site renders identically until an admin
// edits site_settings.global_ctas.sponsorship_label.
const SPONSORSHIP_LABEL_FALLBACK = 'Sponsorship Opportunities';

export default function HeroSection({ hero, sponsorshipUrl, sponsorshipLabel, presentingSponsor, motion = {} }) {
  const headline1 = hero.headline_line1 || 'EDUCATION. COLLABORATION.';
  const headline2 = hero.headline_line2 || 'DEALMAKING.';
  const h1Orange = !!hero.headline_line1_orange;
  const h2Orange = hero.headline_line2_orange !== false;
  const subWords = hero.subhead_words || [];
  const orangeIdx = typeof hero.subhead_orange_index === 'number' ? hero.subhead_orange_index : 2;
  const poster = fileUrl(hero.poster_url || hero.image_url) || 'https://images.unsplash.com/photo-1605833556294-ea5c7a74f57d?w=1600&q=80&auto=format&fit=crop';
  const videoSrc = fileUrl(hero.video_url || '');
  const isVideo = (hero.background_type || 'image') === 'video' && !!videoSrc;
  const overlay = typeof hero.overlay_opacity === 'number' ? hero.overlay_opacity : 0.55;
  const stats = hero.stats || [];

  const ctaPrimaryLabel = hero.cta_primary_label || 'Register Now';
  const ctaPrimaryUrl = hero.cta_primary_url || DEFAULT_SWOOGO;
  // v3.9 — BOOK ROOMS CTA (solid orange primary, fourth row position,
  // links to Bellagio reservation). Visibility-gated by an admin toggle
  // so it can be hidden without deleting the URL.
  const ctaBookRoomsLabel = hero.cta_book_rooms_label || 'Book Rooms';
  const ctaBookRoomsUrl = hero.cta_book_rooms_url || DEFAULT_BELLAGIO_BOOKING;
  const ctaBookRoomsVisible = hero.cta_book_rooms_visible !== false;
  const ctaSpeakersLabel = hero.cta_speakers_label || 'View Speakers';
  const ctaSpeakersUrl = hero.cta_speakers_url || '#speakers';
  // v3.11 — SCHEDULE CTA (outlined secondary, fifth row position, opens
  // the SEICon III Swoogo schedule page). Visibility-gated by an admin
  // toggle so it can be hidden without deleting the URL.
  const ctaScheduleLabel = hero.cta_schedule_label || 'Schedule';
  const ctaScheduleUrl = hero.cta_schedule_url || DEFAULT_SCHEDULE_URL;
  const ctaScheduleVisible = hero.cta_schedule_visible !== false;
  const sponsorshipHref = (sponsorshipUrl && sponsorshipUrl.trim()) ? sponsorshipUrl.trim() : SPONSORSHIP_MAILTO_FALLBACK;
  // v3.17 — Data-driven third-CTA label. Empty/blank admin value falls
  // back to the constant so pre-v3.17 live behavior is preserved.
  const sponsorshipBtnLabel = (sponsorshipLabel && sponsorshipLabel.trim()) ? sponsorshipLabel.trim() : SPONSORSHIP_LABEL_FALLBACK;

  const speedKey = (hero.entrance_speed || 'normal').toLowerCase();
  const speedMul = SPEED_MULTIPLIER[speedKey] !== undefined ? SPEED_MULTIPLIER[speedKey] : 1.0;
  const speedOff = speedKey === 'off';

  const { motionAllowed } = useMotion();
  const entranceEnabled = (motion?.reveals?.hero_entrance !== false) && motionAllowed && hero.entrance_enabled !== false && !speedOff;
  // Hero video autoplays muted on ALL viewports. iOS Safari requires `muted` and
  // `playsInline` to be static JSX attributes (set in HTML), which they are below.
  const showVideo = isVideo;
  const videoRef = useInViewportVideo();

  const handleAnchor = (id) => (e) => {
    if (!id || !id.startsWith('#')) return;
    e.preventDefault();
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const magneticEnabled = motion?.wow?.magnetic_cursor !== false;
  const registerBtnRef = useMagnetic(magneticEnabled);

  const entCls = (cls) => entranceEnabled ? cls : '';
  const entStyle = (delayMs) => entranceEnabled ? { animationDelay: `${Math.max(0, delayMs * speedMul)}ms` } : undefined;

  // ----- Scrim opacities (admin OVERLAY OPACITY drives darkness 0–0.85) -----
  // Desktop/tablet: left-biased gradient that HOLDS the full overlay value across
  // the entire copy zone (0–35%), then falls off gradually so the right side of
  // the video stays visible. Mobile: uniform stronger wash (min 0.65) plus a soft
  // bottom-anchor gradient + a subtle top-anchor fade for nav contrast.
  const desktopScrim = `linear-gradient(to right, rgba(0,0,0,${overlay}) 0%, rgba(0,0,0,${overlay}) 35%, rgba(0,0,0,${(overlay * 0.55).toFixed(3)}) 70%, rgba(0,0,0,${(overlay * 0.25).toFixed(3)}) 100%)`;
  const mobileScrim = `rgba(0,0,0,${Math.max(overlay, 0.65).toFixed(3)})`;
  const mobileBottomAnchor = 'linear-gradient(to top, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 40%)';
  const topAnchor = 'linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 100%)';

  return (
    <section
      id="hero"
      className="relative overflow-hidden bg-card-dark min-h-[90vh]"
      data-testid="section-hero"
    >
      {/* Layer 1 — Full-bleed background (video or fallback image) */}
      <div className={`absolute inset-0 z-0 ${entCls('hero-fade-in')}`} style={entStyle(0)} aria-hidden="true">
        {showVideo ? (
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover"
            poster={poster}
            src={videoSrc}
            muted
            loop
            playsInline
            autoPlay
            preload="metadata"
            aria-hidden
          />
        ) : (
          <img
            src={poster}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
      </div>

      {/* Layer 2 — Scrim (desktop/tablet = admin-controlled left-biased gradient) */}
      <div
        className="absolute inset-0 z-[1] hidden md:block pointer-events-none"
        style={{ background: desktopScrim }}
        aria-hidden="true"
      />
      {/* Layer 2 (mobile) — uniform stronger wash + soft bottom anchor */}
      <div
        className="absolute inset-0 z-[1] md:hidden pointer-events-none"
        style={{ background: mobileScrim }}
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 z-[1] md:hidden pointer-events-none"
        style={{ background: mobileBottomAnchor }}
        aria-hidden="true"
      />

      {/* Layer 2 (top-anchor) — soft fade across all viewports to give the nav area extra contrast */}
      <div
        className="absolute inset-x-0 top-0 h-32 z-[1] pointer-events-none"
        style={{ background: topAnchor }}
        aria-hidden="true"
      />

      {/* Layer 3 — Copy stack, overlaid */}
      <div className="container-x relative z-10 min-h-[90vh] flex items-center pt-20 pb-14 md:py-28 lg:py-32">
        <div className="w-full md:max-w-[70%] lg:max-w-[60%]">
          {/* Hero mark. The white shield replaces the former SEICON / III
              wordmark. `alt` carries the wordmark text so the hero still
              announces itself to screen readers and to search engines.
              Intrinsic width/height are declared so the surrounding copy
              does not shift while the image decodes. The nav-bar logo is a
              separate asset and is deliberately unchanged. */}
          <div className={entCls('hero-step')} style={entStyle(400)}>
            <img
              src="/hero-shield-white.png"
              alt="SEICON III"
              width={388}
              height={512}
              className="h-28 md:h-36 w-auto"
              data-testid="hero-shield"
            />
          </div>
          {/* v3.14 — Location line. Explicit distinction between three
              admin-intended states:
                - Non-blank string (after trim): render as-is.
                - Empty string or whitespace-only: admin cleared the
                  field intentionally. The <p> is REMOVED from the DOM
                  entirely so no top-margin gap remains between the
                  SEICON III title and the date line.
                - null / undefined (field genuinely absent, fresh DB):
                  fall back to the hardcoded default so a brand-new
                  install renders sensible copy.
              `||` is deliberately avoided because it cannot tell an
              intentional blank from a missing field. */}
          {(() => {
            if (typeof hero.location === 'string') {
              return hero.location.trim() === '' ? null : (
                <p className={`mt-3 font-body font-medium text-[12px] md:text-[14px] uppercase text-white tracking-wider4 ${entCls('hero-step')}`} style={entStyle(500)}>
                  {hero.location}
                </p>
              );
            }
            return (
              <p className={`mt-3 font-body font-medium text-[12px] md:text-[14px] uppercase text-white tracking-wider4 ${entCls('hero-step')}`} style={entStyle(500)}>
                {'BELLAGIO HOTEL & CASINO \u2022 LAS VEGAS'}
              </p>
            );
          })()}
          {/* v3.14 — Date line: same three-state distinction as the
              location line above. See rationale on that block. */}
          {(() => {
            if (typeof hero.date === 'string') {
              return hero.date.trim() === '' ? null : (
                <p className={`mt-1 font-display font-semibold uppercase text-brand-red text-[16px] md:text-[18px] tracking-tightish ${entCls('hero-step')}`} style={entStyle(600)}>
                  {hero.date}
                </p>
              );
            }
            return (
              <p className={`mt-1 font-display font-semibold uppercase text-brand-red text-[16px] md:text-[18px] tracking-tightish ${entCls('hero-step')}`} style={entStyle(600)}>
                {'JULY 7\u20139, 2026'}
              </p>
            );
          })()}

          {presentingSponsor && presentingSponsor.enabled && presentingSponsor.show_in_hero !== false && (
            <div className={`mt-5 ${entCls('hero-step')}`} style={entStyle(700)}>
              <PresentingSponsorBadge
                sponsor={presentingSponsor}
                eyebrow={presentingSponsor.hero_eyebrow_label}
                theme="dark"
                size="md"
                testid="hero-presenting-sponsor"
                align="start"
              />
            </div>
          )}

          <h1 className="mt-8 font-display font-bold uppercase leading-[0.95] tracking-tightish text-[40px] md:text-[56px] lg:text-[80px]">
            <span className={`${h1Orange ? 'text-brand-orange' : 'text-white'} ${entCls('hero-blur-clear')}`} style={entStyle(1000)}>{headline1}</span>
            <br />
            <span className={`${h2Orange ? 'text-brand-orange' : 'text-white'} ${entCls('hero-glow')}`} style={entStyle(1300)}>{headline2}</span>
          </h1>

          {subWords.length > 0 && (
            <p className={`mt-6 font-body font-medium text-[14px] md:text-[18px] uppercase text-white tracking-wider2 ${entCls('hero-step')}`} style={entStyle(1800)}>
              {subWords.map((w, i) => (
                <span key={i} className={i === orangeIdx ? 'text-brand-orange' : ''}>
                  {w}{i < subWords.length - 1 ? '\u00A0\u00A0\u00A0' : ''}
                </span>
              ))}
            </p>
          )}

          {/* v3.11 — five-CTA hero row. lg:flex-nowrap was removed (was
              added in v3.10 for the 4-CTA case) because at 5 buttons the
              natural total width (~946px at full content) cannot fit the
              ~730px copy column without crushing text inside the buttons.
              The row now wraps naturally on every breakpoint where the
              flex direction is row, producing a balanced 3+2 layout on
              desktop (REGISTER + BOOK ROOMS + VIEW SPEAKERS on line 1,
              SPONSORSHIP + SCHEDULE on line 2). Tablet wrap and mobile
              column-stack behavior are unchanged. */}
          <div className="mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
            <span ref={registerBtnRef} className={`inline-block ${entCls('hero-step')}`} style={entStyle(2100)}>
              <a href={ctaPrimaryUrl} target={ctaPrimaryUrl && !ctaPrimaryUrl.startsWith('#') ? '_blank' : undefined} rel="noopener noreferrer" className="btn-primary" data-testid="hero-register">{ctaPrimaryLabel}</a>
            </span>
            {ctaBookRoomsVisible && (
              <span className={`inline-block ${entCls('hero-step')}`} style={entStyle(2150)}>
                <a href={ctaBookRoomsUrl} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="hero-book-rooms">{ctaBookRoomsLabel}</a>
              </span>
            )}
            <span className={`inline-block ${entCls('hero-step')}`} style={entStyle(2200)}>
              <a href={ctaSpeakersUrl} onClick={ctaSpeakersUrl.startsWith('#') ? handleAnchor(ctaSpeakersUrl) : undefined} className="btn-secondary !border-white !text-white hover:!bg-white hover:!text-card-dark" data-testid="hero-speakers">{ctaSpeakersLabel}</a>
            </span>
            <span className={`inline-block ${entCls('hero-step')}`} style={entStyle(2300)}>
              <a href={sponsorshipHref} target={sponsorshipHref.startsWith('http') ? '_blank' : undefined} rel={sponsorshipHref.startsWith('http') ? 'noopener noreferrer' : undefined} className="btn-secondary !border-white !text-white hover:!bg-white hover:!text-card-dark" data-testid="hero-sponsorship">{sponsorshipBtnLabel}</a>
            </span>
            {ctaScheduleVisible && (
              <span className={`inline-block ${entCls('hero-step')}`} style={entStyle(2400)}>
                <a href={ctaScheduleUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary !border-white !text-white hover:!bg-white hover:!text-card-dark" data-testid="hero-schedule">{ctaScheduleLabel}</a>
              </span>
            )}
          </div>

          <div
            className={`mt-12 grid grid-cols-1 md:grid-cols-5 border border-white/15 rounded-md bg-black/30 backdrop-blur-sm overflow-hidden ${entCls('hero-step')}`}
            style={entStyle(2400)}
          >
            {stats.map((s, i) => (
              <StatCell key={i} stat={s} index={i} tickerEnabled={motion?.wow?.number_ticker !== false} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function StatCell({ stat, index, tickerEnabled }) {
  const Icon = iconMap[stat.icon] || Calendar;
  const { num, suffix } = parseNumber(stat.number);
  const { ref, value } = useNumberTicker(num, { enabled: tickerEnabled });
  const { motionAllowed } = useMotion();
  const display = (motionAllowed && tickerEnabled) ? `${formatNumber(value)}${suffix}` : stat.number;

  return (
    <div
      ref={ref}
      className={`py-5 px-3 flex flex-row items-center gap-3 md:flex-col md:items-start md:gap-1 ${index > 0 ? 'border-t md:border-t-0 md:border-l border-white/15' : ''}`}
    >
      <Icon size={18} className={`text-brand-orange shrink-0 ${motionAllowed ? 'stat-pulse' : ''}`} />
      <div className="font-display font-bold text-white text-[28px] md:text-[28px] lg:text-[32px] leading-none tracking-tightish mt-0 md:mt-1 shrink-0">{display}</div>
      <div className="font-body font-medium text-[10px] md:text-[11px] uppercase text-white/70 tracking-wider3 mt-0 md:mt-1">{stat.label}</div>
    </div>
  );
}
